import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import LiveBoard from "./dashboard/LiveBoard";
import RecordsLog from "./dashboard/RecordsLog";
import StaffManager from "./dashboard/StaffManager";
import Settings from "./dashboard/SettingsPanel";
import AuditLog from "./dashboard/AuditLog";

const BASE_TABS = [
  ["live", "Now"],
  ["week", "Records"],
  ["staff", "Staff"],
  ["settings", "Settings"],
];

export default function Dashboard() {
  const nav = useNavigate();
  const { logout, authed, loading, brand, reloadBrand, prefs, me } = useAuth();
  const [tab, setTab] = useState("live");
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pendingOffender, setPendingOffender] = useState(null);
  const signingOut = useRef(false);

  const jumpToOffender = useCallback((offender) => {
    setPendingOffender(offender);
    setTab("week");
  }, []);

  const isSuper = !!me?.is_super;
  const tabs = isSuper ? [...BASE_TABS, ["audit", "Audit"]] : BASE_TABS;

  const load = useCallback(async () => {
    try {
      const [b, e] = await Promise.all([
        client.get("/branches"),
        client.get("/employees"),
      ]);
      setBranches(b.data.list || []);
      setEmployees(e.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (!loading && !authed && !signingOut.current) nav("/owner-access");
  }, [authed, loading, nav]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  useEffect(() => {
    if (!authed) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [authed, load]);

  // Auto sign-out on inactivity — timestamp-based so it survives the browser
  // pausing timers while the tab is in the background (screen off, other app on top).
  useEffect(() => {
    if (!authed) return;
    const minutes = prefs?.auto_signout_min || 5;
    const idleMs = minutes * 60 * 1000;
    let lastActivity = Date.now();
    const doLogout = () => {
      signingOut.current = true;
      logout();
      toast("Signed out after inactivity");
      nav("/", { replace: true });
    };
    const bump = () => { lastActivity = Date.now(); };
    const check = () => {
      if (Date.now() - lastActivity >= idleMs) doLogout();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    const poller = setInterval(check, 15000);
    return () => {
      clearInterval(poller);
      events.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [authed, prefs?.auto_signout_min, logout, nav]);

  if (loading || !authed) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <p className="text-muted-jewel caps">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-ivory font-body">
      <div className="max-w-[560px] mx-auto px-5 pb-24">
        <div className="flex items-center justify-between pt-6 pb-2">
          <button
            onClick={() => nav("/")}
            data-testid="brand-home"
            className="text-left"
          >
            <div className="flex items-center gap-2">
              <div className="font-serif text-[26px] font-semibold text-ivory tracking-wide leading-tight">
                {brand?.title || "Register"}
              </div>
              {isSuper && (
                <span
                  data-testid="header-super-badge"
                  className="text-[10px] font-semibold tracking-widest uppercase text-gold border border-gold-deep rounded-full px-2 py-0.5"
                >
                  Super
                </span>
              )}
            </div>
            <div className="caps-lg text-gold mt-1">
              {me?.label ? `${me.label} · Owner panel` : "Owner panel"}
            </div>
          </button>
          <button
            onClick={() => { signingOut.current = true; logout(); nav("/", { replace: true }); }}
            data-testid="dash-signout"
            className="rounded-full border border-line text-muted-jewel px-4 py-2 text-sm hover:text-ivory hover:border-gold-deep transition-colors"
          >
            Sign out
          </button>
        </div>

        <div className="flex gap-2 flex-wrap mt-5 mb-6">
          {tabs.map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors border ${
                tab === k
                  ? "seal-primary border-transparent font-semibold"
                  : "bg-transparent border-line text-muted-jewel hover:text-ivory"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "live" && <LiveBoard branches={branches} employees={employees} onRefresh={load} onOffenderJump={jumpToOffender} />}
        {tab === "week" && <RecordsLog employees={employees} onRefresh={load} pendingOffender={pendingOffender} onOffenderConsumed={() => setPendingOffender(null)} />}
        {tab === "staff" && <StaffManager branches={branches} employees={employees} onRefresh={load} />}
        {tab === "settings" && (
          <Settings
            branches={branches}
            onRefresh={load}
            brand={brand}
            reloadBrand={reloadBrand}
            isSuper={isSuper}
            me={me}
          />
        )}
        {tab === "audit" && isSuper && <AuditLog />}
      </div>
    </div>
  );
}
